import React, { createContext, FC, ReactNode, useContext } from 'react'
import { BufferGeometry } from 'three'
import {
  ComponentSurfaceWrappingMode,
  GeckoComponentModel,
  GeckoComponentRect,
} from 'types'

type ComponentSurfaceContextType = Readonly<{
  /** The bounds of this surface's UVs in 2D component space */
  bounds: Readonly<GeckoComponentRect>
  /** The mesh to draw this point on */
  geometry: Readonly<BufferGeometry>
  /** Whether this surface wraps in the x direction */
  wrapMode: ComponentSurfaceWrappingMode
  /** Component model that this surface represents */
  componentModel: GeckoComponentModel | undefined
}>

const ComponentSurfaceContext = createContext<
  ComponentSurfaceContextType | undefined
>(undefined)

export const SurfaceProvider: FC<
  ComponentSurfaceContextType & { children: ReactNode }
> = ({ children, ...props }) => (
  <ComponentSurfaceContext.Provider value={props}>
    {children}
  </ComponentSurfaceContext.Provider>
)

export const useSurfaceProps = (): ComponentSurfaceContextType => {
  const context = useContext(ComponentSurfaceContext)
  if (!context)
    throw new Error('useSurfaceProps() must be used within a Surface3D!')
  return context
}

type ComponentModelSurfaceContextType = Readonly<{
  /** Component model that this surface represents */
  componentModel: GeckoComponentModel
}>

const ComponentModelSurfaceContext = createContext<
  ComponentModelSurfaceContextType | undefined
>(undefined)

export const ModelSurfaceProvider: FC<
  ComponentModelSurfaceContextType & { children: ReactNode }
> = ({ children, ...props }) => (
  <ComponentModelSurfaceContext.Provider value={props}>
    {children}
  </ComponentModelSurfaceContext.Provider>
)

export const useModelSurfaceProps = (): ComponentModelSurfaceContextType => {
  const context = useContext(ComponentModelSurfaceContext)
  if (!context)
    throw new Error(
      'useModelSurfaceData() must be used within a ModelSurface3D!'
    )
  return context
}
